/**
 * Quarantine protocols (ICA 29, PS 27 and the like): the states each covers,
 * its mix, the litres each tree type takes, and the designated spray areas it
 * may be carried out in. Plan: docs/ica-quarantine-treatment-plan.md.
 */

import type { NumericValue } from '@/utils/numeric';

export const QUARANTINE_CATEGORIES = [
  'insecticide', 'fungicide', 'miticide', 'molluscicide', 'wetter', 'systemic', 'herbicide', 'other',
] as const;
export type QuarantineCategory = typeof QUARANTINE_CATEGORIES[number];

export const QUARANTINE_CATEGORY_LABEL: Record<QuarantineCategory, string> = {
  insecticide: 'Insecticide', fungicide: 'Fungicide', miticide: 'Miticide', molluscicide: 'Molluscicide',
  wetter: 'Wetter', systemic: 'Systemic', herbicide: 'Herbicide', other: 'Other',
};

export const QUARANTINE_UNITS = ['mL', 'L', 'g', 'kg'] as const;
export type QuarantineUnit = typeof QUARANTINE_UNITS[number];

/**
 * How a chemical goes on: a soil drench at a set amount per pot, or sprayed to
 * run-off; a chemical can be in both mixes (Rob, 2026-10-14, ICA 29).
 */
export type QuarantineMethod = 'drench' | 'run_off';
export type QuarantineAppliesTo = QuarantineMethod | 'both';
export const QUARANTINE_METHOD_LABEL: Record<QuarantineMethod, string> = {
  drench: 'Soil drench', run_off: 'Spray to run-off',
};
export const APPLIES_TO_LABEL: Record<QuarantineAppliesTo, string> = {
  drench: 'Drench', run_off: 'Run-off', both: 'Both',
};

export const AU_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

export interface QuarantineProtocolChemical {
  id?: number;
  category: QuarantineCategory;
  chemical_id: number;
  chemical_name?: string;
  /** The mixing rate: this much in every 100 L of mix. */
  rate_per_100l: NumericValue;
  rate_unit: QuarantineUnit;
  applies_to?: QuarantineAppliesTo;
}

export interface QuarantinePotTreatment {
  id?: number;
  chemical_id: number;
  chemical_name?: string;
  amount_per_pot: NumericValue;
  unit: QuarantineUnit;
  instructions: string | null;
}

export interface QuarantineVolume {
  tree_type_id: number;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  /** Litres of drench mix per pot; null when the tree type is not drenched (bare-root). */
  litres_per_plant: NumericValue | null;
  /** Sprayed to run-off: the tanks say how much went on. A tree type can be drenched too. */
  to_run_off: boolean;
}

export interface QuarantineArea {
  location_id: number;
  name: string;
  farm_name: string | null;
  active: boolean;
}

export interface QuarantineProtocol {
  id: number;
  organisation_id: number;
  code: string;
  name: string;
  destination_states: string[];
  window_hours: number;
  instructions: string | null;
  controller_name: string | null;
  controller_user_id: number | null;
  validator_user_ids: number[];
  active: boolean;
  chemicals: QuarantineProtocolChemical[];
  pot_treatments: QuarantinePotTreatment[];
  volumes: QuarantineVolume[];
  areas: QuarantineArea[];
  created_at: string;
  updated_at: string;
}

export interface QuarantineProtocolPayload {
  code?: string;
  name?: string;
  destination_states?: string[];
  window_hours?: number;
  instructions?: string | null;
  /** One of the organisation's people; the server takes the printed name from them. */
  controller_user_id?: number | null;
  active?: boolean;
  chemicals?: {
    category: QuarantineCategory; chemical_id: number; rate_per_100l: number; rate_unit: QuarantineUnit;
    applies_to: QuarantineAppliesTo;
  }[];
  pot_treatments?: { chemical_id: number; amount_per_pot: number; unit: QuarantineUnit; instructions: string | null }[];
  volumes?: { tree_type_id: number; litres_per_plant: number | null; to_run_off: boolean }[];
  area_location_ids?: number[];
}

// ── A load's treatments (Phase 1) ──

export type QuarantineTreatmentState =
  | 'cancelled' | 'awaiting_spray' | 'awaiting_validation' | 'treated' | 'expired';

export interface QuarantinePlantsRow {
  tree_type_id: number;
  tree_type_name: string | null;
  plants: number;
  litres_per_plant: number | null;
  to_run_off: boolean;
  litres: number | null;
}

export interface QuarantinePlantsPlan {
  plants_by_tree_type: QuarantinePlantsRow[];
  total_plants: number;
  planned_litres: number;
  run_off_plants: number;
  missing: { tree_type_id: number; tree_type_name: string | null; plants: number }[];
}

export interface QuarantineTreatment {
  id: number;
  load_id: number;
  protocol_id: number;
  spray_application_id: number | null;
  area_location_id: number | null;
  area_name: string | null;
  order_ids: number[];
  total_plants: number;
  planned_litres: NumericValue | null;
  count_basis: 'picked' | 'ordered';
  /** 'all' for a treatment made before pots and run-off were split. */
  method?: QuarantineMethod | 'all';
  spray_status: string | null;
  reference_number: string | null;
  scheduled_date: string | null;
  completed_at: string | null;
  operator_name: string | null;
  signed_off_by_name: string | null;
  validated_at: string | null;
  validated_by_name: string | null;
  state: QuarantineTreatmentState;
  valid_until: string | null;
  created_at: string;
}

export interface QuarantineNeed extends QuarantinePlantsPlan {
  protocol: QuarantineProtocol;
  orders: {
    order_id: number; order_number: string; customer_name: string | null; state: string; plants: number; covered: boolean;
    phac_number: string | null;
    methods?: QuarantineMethod[];
  }[];
  untreated_order_ids: number[];
  untreated_plan: QuarantinePlantsPlan;
  /** Per mix: the orders still to treat with it, their plants, and the chemicals it draws on. */
  untreated_by_method?: Record<QuarantineMethod, {
    order_ids: number[]; plan: QuarantinePlantsPlan; chemicals: QuarantineProtocolChemical[];
  }>;
  treatments: QuarantineTreatment[];
}

export interface LoadQuarantine {
  load: { id: number; load_number: string; status: string; despatch_farm_id: number; despatch_farm_name: string | null };
  count_basis: 'picked' | 'ordered';
  needs: QuarantineNeed[];
  no_state: { order_id: number; order_number: string; customer_name: string | null }[];
  /** Trees under a protocol with no botanical name recorded: the species sheet cannot name them. */
  species_missing?: { crop_type_id: number | null; crop_type_name: string | null; cultivars: string[] }[];
}

export interface CreateQuarantineTreatmentPayload {
  protocol_id: number;
  area_location_id: number;
  chemical_ids: number[];
  scheduled_date: string;
  notes?: string | null;
  method?: QuarantineMethod;
}

/** A quarantine treatment a lot went out under, through its picks (Phase 4). */
export interface LotQuarantineEntry {
  id: number;
  load_id: number;
  load_number: string;
  despatch_date: string;
  load_status: string;
  code: string;
  name: string;
  window_hours: number;
  method?: QuarantineMethod | 'all';
  spray_application_id: number | null;
  reference_number: string | null;
  spray_status: string | null;
  completed_at: string | null;
  validated_at: string | null;
  validated_by_name: string | null;
  area_name: string | null;
  trees: number;
  orders: { order_id: number; order_number: string; phac_number: string | null }[];
  state: QuarantineTreatmentState;
  valid_until: string | null;
}
